import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FiArrowLeft, FiEdit2, FiCheck, FiX, FiBell, FiPlus, FiPhoneCall, 
  FiMail, FiMessageSquare, FiCalendar, FiUser, FiHome, FiDollarSign, 
  FiClock, FiStar, FiFileText, FiTrash2, FiExternalLink, FiMapPin 
} from 'react-icons/fi';
import { leadAPI, reminderAPI, clientAPI, propertyAPI } from '../../services/api';
import Modal from '../../components/shared/Modal';

const STATUS_FLOW = ['new', 'contacted', 'qualified', 'proposal', 'negotiation'];

const STATUS_CONFIG = {
  new:         { label: 'Nouveau',      color: '#3b82f6', bg: '#dbeafe' },
  contacted:   { label: 'Contacté',     color: '#f59e0b', bg: '#fef3c7' },
  qualified:   { label: 'Qualifié',     color: '#8b5cf6', bg: '#ede9fe' },
  proposal:    { label: 'Proposition',  color: '#06b6d4', bg: '#cffaff' },
  negotiation: { label: 'Négociation',  color: '#f97316', bg: '#ffedd5' },
  won:         { label: 'Gagné (Client)', color: '#22c55e', bg: '#dcfce7' },
  lost:        { label: 'Perdu',        color: '#ef4444', bg: '#fee2e2' },
};

const PRIORITY_CONFIG = {
  low:    { label: 'Basse',   cls: 'badge-secondary' },
  medium: { label: 'Moyenne', cls: 'badge-info' },
  high:   { label: 'Haute',   cls: 'badge-warning' },
  urgent: { label: 'Urgente', cls: 'badge-danger' },
};

const INTERACTION_TYPES = [
  { value: 'call',    label: 'Appel téléphonique', icon: <FiPhoneCall /> },
  { value: 'email',   label: 'Email envoyé / reçu', icon: <FiMail /> },
  { value: 'meeting', label: 'Réunion / Rendez-vous', icon: <FiCalendar /> },
  { value: 'visit',   label: 'Visite de bien',      icon: <FiHome /> },
  { value: 'note',    label: 'Note interne',       icon: <FiFileText /> },
  { value: 'message', label: 'Message / WhatsApp', icon: <FiMessageSquare /> },
];

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');

  // Modal d'édition
  const [showEditModal, setShowEditModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [editForm, setEditForm] = useState({});

  // Modal Interaction
  const [showInterModal, setShowInterModal] = useState(false);
  const [interForm, setInterForm] = useState({
    type: 'call',
    subject: '',
    content: '',
    outcome: '',
  });

  // Modal Rappel
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderForm, setReminderForm] = useState({ title: '', remind_at: '', description: '' });

  const fetchLeadData = async () => {
    try {
      const [leadRes, interRes] = await Promise.all([
        leadAPI.getById(id),
        leadAPI.getInteractions(id),
      ]);
      setLead(leadRes.data);
      setInteractions(interRes.data || []);
      setLoading(false);
    } catch (e) {
      console.error('Erreur chargement lead:', e);
      navigate('/leads');
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [cliRes, propRes] = await Promise.all([
        clientAPI.list({ limit: 100 }),
        propertyAPI.list({ limit: 100 }),
      ]);
      const cliList = Array.isArray(cliRes.data) ? cliRes.data : (cliRes.data?.clients || cliRes.data?.data || []);
      const propList = Array.isArray(propRes.data) ? propRes.data : (propRes.data?.properties || propRes.data?.data || []);
      setClients(cliList);
      setProperties(propList);
    } catch (e) {
      setClients([]);
      setProperties([]);
    }
  };

  useEffect(() => {
    fetchLeadData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const handleStatusChange = async (newStatus) => {
    if (lead.status === newStatus) return;
    try {
      await leadAPI.update(id, { status: newStatus });
      fetchLeadData();
    } catch (e) {
      alert('Erreur lors du changement de statut');
    }
  };

  const handleConvert = async () => {
    if (!window.confirm('Convertir ce lead en client ? Un dossier client actif sera configuré.')) return;
    try {
      const res = await leadAPI.convert(id);
      alert('Lead converti avec succès en client !');
      if (res.data?.clientId) {
        navigate(`/clients/${res.data.clientId}`);
      } else {
        fetchLeadData();
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la conversion');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce lead ?')) return;
    try {
      await leadAPI.delete(id);
      navigate('/leads');
    } catch (e) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleOpenEdit = () => {
    setEditForm({
      client_id: lead.client_id || '',
      property_id: lead.property_id || '',
      status: lead.status || 'new',
      priority: lead.priority || 'medium',
      source: lead.source || '',
      budget_min: lead.budget_min || '',
      budget_max: lead.budget_max || '',
      desired_type: lead.desired_type || '',
      desired_location: lead.desired_location || '',
      score: lead.score || 30,
      notes: lead.notes || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editForm,
        client_id: editForm.client_id || null,
        property_id: editForm.property_id || null,
        budget_min: editForm.budget_min ? parseFloat(editForm.budget_min) : null,
        budget_max: editForm.budget_max ? parseFloat(editForm.budget_max) : null,
        score: parseInt(editForm.score || 30),
      };
      await leadAPI.update(id, payload);
      setShowEditModal(false);
      fetchLeadData();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    try {
      await leadAPI.addInteraction(id, interForm);
      setShowInterModal(false);
      setInterForm({ type: 'call', subject: '', content: '', outcome: '' });
      fetchLeadData();
    } catch (e) {
      alert('Erreur lors de l\'enregistrement de l\'interaction');
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    try {
      await reminderAPI.create({ ...reminderForm, lead_id: id });
      setShowReminderModal(false);
      setReminderForm({ title: '', remind_at: '', description: '' });
      alert('Rappel programmé avec succès');
    } catch (e) {
      alert('Erreur lors de la création du rappel');
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!lead) return null;

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
  const statusCfg = STATUS_CONFIG[lead.status] || { label: lead.status, color: '#64748b', bg: '#f1f5f9' };
  const prioCfg = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.medium;
  const currentStepIdx = STATUS_FLOW.indexOf(lead.status);

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/leads')} title="Retour">
            <FiArrowLeft />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0 }}>
                {lead.client_first_name || lead.client_last_name 
                  ? `${lead.client_first_name || ''} ${lead.client_last_name || ''}`.trim()
                  : `Piste #${lead.id.slice(0, 8)}`}
              </h1>
              <span className="badge" style={{ background: statusCfg.bg, color: statusCfg.color, fontWeight: 700 }}>
                {statusCfg.label}
              </span>
              <span className={`badge ${prioCfg.cls}`}>
                {prioCfg.label}
              </span>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              Piste créée le {new Date(lead.created_at).toLocaleDateString('fr-FR')} • Source: {lead.source || 'N/A'}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={handleOpenEdit}>
            <FiEdit2 style={{ marginRight: 4 }} /> Modifier
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowInterModal(true)}>
            <FiPhoneCall style={{ marginRight: 4 }} /> Loguer Activité
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowReminderModal(true)}>
            <FiBell style={{ marginRight: 4 }} /> Rappel
          </button>
          {!['won', 'lost'].includes(lead.status) && (
            <button className="btn btn-success btn-sm" onClick={handleConvert}>
              <FiCheck style={{ marginRight: 4 }} /> Convertir en Client
            </button>
          )}
          <button className="btn btn-danger btn-sm" onClick={handleDelete} title="Supprimer">
            <FiTrash2 />
          </button>
        </div>
      </div>

      {/* Barre de progression visuelle du statut */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-body" style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
              Étape du Pipeline Commercial
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Score de qualification:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f1f5f9', padding: '4px 10px', borderRadius: 16, fontWeight: 700, color: 'var(--primary)' }}>
                <FiStar style={{ color: '#f59e0b' }} /> {lead.score || 0}%
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {STATUS_FLOW.map((stepKey, idx) => {
              const isDone = idx < currentStepIdx || lead.status === 'won';
              const isCurrent = stepKey === lead.status;
              const cfg = STATUS_CONFIG[stepKey];

              let bg = '#f1f5f9';
              let text = 'var(--text-secondary)';
              let border = '1px solid var(--border)';

              if (isCurrent) {
                bg = cfg.color;
                text = '#fff';
                border = `1px solid ${cfg.color}`;
              } else if (isDone) {
                bg = '#dcfce7';
                text = '#15803d';
                border = '1px solid #86efac';
              }

              return (
                <React.Fragment key={stepKey}>
                  <div
                    onClick={() => handleStatusChange(stepKey)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 20,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: bg,
                      color: text,
                      border: border,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      transition: 'all 0.2s ease',
                      boxShadow: isCurrent ? '0 4px 10px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    {isDone && <FiCheck style={{ fontSize: 14 }} />}
                    {cfg.label}
                  </div>
                  {idx < STATUS_FLOW.length - 1 && (
                    <span style={{ color: 'var(--border)', fontSize: 18 }}>→</span>
                  )}
                </React.Fragment>
              );
            })}

            {/* Boutons d'état final Gagné / Perdu */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button
                className={`btn btn-sm ${lead.status === 'won' ? 'btn-success' : 'btn-secondary'}`}
                onClick={() => handleStatusChange('won')}
              >
                <FiCheck /> Gagné
              </button>
              <button
                className={`btn btn-sm ${lead.status === 'lost' ? 'btn-danger' : 'btn-secondary'}`}
                onClick={() => handleStatusChange('lost')}
              >
                <FiX /> Perdu
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 24 }}>
        {[
          { key: 'info',         label: '📋 Informations & Préférences' },
          { key: 'interactions', label: `📞 Activités & Historique (${interactions.length})` },
        ].map((t) => (
          <button
            key={t.key}
            className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB 1: INFORMATIONS */}
      {tab === 'info' && (
        <div className="grid-2">
          {/* Colonne gauche: Client & Bien */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Fiche Client */}
            <div className="card">
              <div className="card-header">
                <h2><FiUser /> Prospect / Client Associé</h2>
              </div>
              <div className="card-body">
                {lead.client_id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div className="detail-row">
                      <div className="detail-label">Nom complet</div>
                      <div className="detail-value">
                        <Link to={`/clients/${lead.client_id}`} style={{ fontWeight: 700, color: 'var(--primary)', textDecoration: 'none' }}>
                          {lead.client_first_name} {lead.client_last_name} <FiExternalLink style={{ fontSize: 12 }} />
                        </Link>
                      </div>
                    </div>
                    {lead.client_email && (
                      <div className="detail-row">
                        <div className="detail-label">Email</div>
                        <div className="detail-value">{lead.client_email}</div>
                      </div>
                    )}
                    {lead.client_phone && (
                      <div className="detail-row">
                        <div className="detail-label">Téléphone</div>
                        <div className="detail-value">{lead.client_phone}</div>
                      </div>
                    )}
                    <div className="detail-row">
                      <div className="detail-label">Catégorie CRM</div>
                      <div className="detail-value"><span className="badge badge-info">{lead.client_category || 'prospect'}</span></div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-secondary)' }}>
                    <p style={{ margin: '0 0 12px 0' }}>Aucun client formellement associé à cette piste.</p>
                    <button className="btn btn-secondary btn-sm" onClick={handleOpenEdit}>
                      Associer un client
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bien Immobilier cible */}
            <div className="card">
              <div className="card-header">
                <h2><FiHome /> Bien Immobilier Ciblé</h2>
              </div>
              <div className="card-body">
                {lead.property_id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div className="detail-row">
                      <div className="detail-label">Titre du bien</div>
                      <div className="detail-value">
                        <Link to={`/properties/${lead.property_id}`} style={{ fontWeight: 700, color: 'var(--primary)', textDecoration: 'none' }}>
                          {lead.property_title} <FiExternalLink style={{ fontSize: 12 }} />
                        </Link>
                      </div>
                    </div>
                    {lead.property_city && (
                      <div className="detail-row">
                        <div className="detail-label">Localisation</div>
                        <div className="detail-value">📍 {lead.property_address || ''} {lead.property_city}</div>
                      </div>
                    )}
                    {lead.property_price && (
                      <div className="detail-row">
                        <div className="detail-label">Prix affiché</div>
                        <div className="detail-value" style={{ fontWeight: 700, color: 'var(--primary)' }}>
                          {fmt(lead.property_price)} XOF
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 13 }}>
                    Recherche globale (aucun bien spécifique lié).
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Colonne droite: Critères de recherche & Paramètres CRM */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Critères de recherche */}
            <div className="card">
              <div className="card-header">
                <h2><FiDollarSign /> Critères & Budget</h2>
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="detail-row">
                  <div className="detail-label">Budget Minimum</div>
                  <div className="detail-value">{lead.budget_min ? `${fmt(lead.budget_min)} XOF` : 'Non renseigné'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Budget Maximum</div>
                  <div className="detail-value" style={{ fontWeight: 700, color: 'var(--primary)' }}>
                    {lead.budget_max ? `${fmt(lead.budget_max)} XOF` : 'Non renseigné'}
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Type de bien désiré</div>
                  <div className="detail-value">{lead.desired_type || 'Tous types'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Localisation souhaitée</div>
                  <div className="detail-value">{lead.desired_location ? `📍 ${lead.desired_location}` : 'Toutes zones'}</div>
                </div>
              </div>
            </div>

            {/* Suivi CRM & Agent */}
            <div className="card">
              <div className="card-header">
                <h2><FiStar /> Paramètres du Lead</h2>
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="detail-row">
                  <div className="detail-label">Source d'acquisition</div>
                  <div className="detail-value"><span className="badge badge-secondary">{lead.source || 'Inconnue'}</span></div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Agent Responsable</div>
                  <div className="detail-value" style={{ fontWeight: 600 }}>
                    {lead.assigned_first_name ? `${lead.assigned_first_name} ${lead.assigned_last_name || ''}` : 'Non assigné'}
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Dernière mise à jour</div>
                  <div className="detail-value">{new Date(lead.updated_at || lead.created_at).toLocaleString('fr-FR')}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Notes & Observations</div>
                  <div className="detail-value" style={{ whiteSpace: 'pre-wrap', fontStyle: lead.notes ? 'normal' : 'italic', color: lead.notes ? 'inherit' : 'var(--text-secondary)' }}>
                    {lead.notes || 'Aucune note spécifique enregistrée.'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: INTERACTIONS */}
      {tab === 'interactions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Journal des Activités et Échanges</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowInterModal(true)}>
              <FiPlus style={{ marginRight: 4 }} /> Enregistrer une Activité
            </button>
          </div>

          <div className="card">
            <div className="card-body" style={{ padding: 20 }}>
              {interactions.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {interactions.map((inter) => {
                    const typeObj = INTERACTION_TYPES.find((t) => t.value === inter.type) || INTERACTION_TYPES[0];
                    return (
                      <div
                        key={inter.id}
                        style={{
                          padding: 16,
                          borderRadius: 8,
                          border: '1px solid var(--border)',
                          background: '#fff',
                          display: 'flex',
                          gap: 16,
                          alignItems: 'flex-start',
                        }}
                      >
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'var(--primary-light || #7c3aed15)',
                          color: 'var(--primary)',
                          display: 'flex',
                          alignItems: 'center',
                          justify: 'center',
                          fontSize: 18,
                          flexShrink: 0,
                        }}>
                          {typeObj.icon}
                        </div>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>
                              {inter.subject || typeObj.label}
                            </span>
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              <FiClock style={{ marginRight: 4 }} />
                              {new Date(inter.interaction_date || inter.created_at).toLocaleString('fr-FR')}
                            </span>
                          </div>

                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                            Par: <strong>{inter.first_name ? `${inter.first_name} ${inter.last_name || ''}` : 'Agent'}</strong>
                            {inter.outcome && (
                              <span className="badge badge-info" style={{ marginLeft: 8 }}>
                                Résultat: {inter.outcome}
                              </span>
                            )}
                          </div>

                          {inter.content && (
                            <p style={{ margin: 0, fontSize: 13, background: '#f8fafc', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                              {inter.content}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
                  <FiPhoneCall style={{ fontSize: 32, marginBottom: 8, color: '#cbd5e1' }} />
                  <p style={{ margin: 0 }}>Aucune activité loguée pour le moment.</p>
                  <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => setShowInterModal(true)}>
                    Consigner un premier appel ou email
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL ÉDITION LEAD */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Modifier le Lead">
        <form onSubmit={handleSaveEdit}>
          <div className="grid-2">
            <div className="form-group">
              <label>Client / Prospect</label>
              <select
                className="form-control"
                value={editForm.client_id || ''}
                onChange={(e) => setEditForm({ ...editForm, client_id: e.target.value })}
              >
                <option value="">-- Aucun client --</option>
                {(Array.isArray(clients) ? clients : []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name} ({c.email || 'Prospect'})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Bien Immobilier</label>
              <select
                className="form-control"
                value={editForm.property_id || ''}
                onChange={(e) => setEditForm({ ...editForm, property_id: e.target.value })}
              >
                <option value="">-- Aucun bien --</option>
                {(Array.isArray(properties) ? properties : []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} ({p.city || ''})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Statut</label>
              <select
                className="form-control"
                value={editForm.status || 'new'}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Priorité</label>
              <select
                className="form-control"
                value={editForm.priority || 'medium'}
                onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
              >
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Source</label>
              <input
                className="form-control"
                value={editForm.source || ''}
                onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Score (0-100%)</label>
              <input
                type="number"
                min="0"
                max="100"
                className="form-control"
                value={editForm.score || 0}
                onChange={(e) => setEditForm({ ...editForm, score: e.target.value })}
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Budget Min (XOF)</label>
              <input
                type="number"
                className="form-control"
                value={editForm.budget_min || ''}
                onChange={(e) => setEditForm({ ...editForm, budget_min: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Budget Max (XOF)</label>
              <input
                type="number"
                className="form-control"
                value={editForm.budget_max || ''}
                onChange={(e) => setEditForm({ ...editForm, budget_max: e.target.value })}
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Type souhaité</label>
              <input
                className="form-control"
                value={editForm.desired_type || ''}
                onChange={(e) => setEditForm({ ...editForm, desired_type: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Lieu souhaité</label>
              <input
                className="form-control"
                value={editForm.desired_location || ''}
                onChange={(e) => setEditForm({ ...editForm, desired_location: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              className="form-control"
              rows={3}
              value={editForm.notes || ''}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary">
              Enregistrer
            </button>
          </div>
        </form>
      </Modal>

      {/* MODAL LOGUER INTERACTION */}
      <Modal isOpen={showInterModal} onClose={() => setShowInterModal(false)} title="Loguer une Activité Commerciale">
        <form onSubmit={handleAddInteraction}>
          <div className="form-group">
            <label>Type d'activité</label>
            <select
              className="form-control"
              value={interForm.type}
              onChange={(e) => setInterForm({ ...interForm, type: e.target.value })}
            >
              {INTERACTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Sujet / Intitulé *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: Premier contact téléphonique, Visite de l'appartement A2..."
              value={interForm.subject}
              onChange={(e) => setInterForm({ ...interForm, subject: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Résultat / Issue</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: Intéressé, À recontacter mardi, Offre transmise..."
              value={interForm.outcome}
              onChange={(e) => setInterForm({ ...interForm, outcome: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Compte-rendu / Détails</label>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Notes détaillées de l'échange..."
              value={interForm.content}
              onChange={(e) => setInterForm({ ...interForm, content: e.target.value })}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowInterModal(false)}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary">
              Consigner
            </button>
          </div>
        </form>
      </Modal>

      {/* MODAL RAPPEL */}
      <Modal isOpen={showReminderModal} onClose={() => setShowReminderModal(false)} title="Créer un Rappel de Suivi">
        <form onSubmit={handleAddReminder}>
          <div className="form-group">
            <label>Titre du rappel *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: Relancer le prospect pour l'offre"
              value={reminderForm.title}
              onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Date et heure d'échéance *</label>
            <input
              type="datetime-local"
              className="form-control"
              value={reminderForm.remind_at}
              onChange={(e) => setReminderForm({ ...reminderForm, remind_at: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Notes / Instructions</label>
            <textarea
              className="form-control"
              rows={3}
              value={reminderForm.description}
              onChange={(e) => setReminderForm({ ...reminderForm, description: e.target.value })}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowReminderModal(false)}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary">
              Programmer le rappel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LeadDetailPage;
